import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import { jwtDecode } from "jwt-decode";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  pagePrimaryButtonSx,
  pageFieldSx,
} from "../../styles/pageModernStyles";
import { getUserById } from "../../api/User/userApi";
import {
  cancelSkillAgentTask,
  createSkillAgentTask,
  deleteSkillAgentTask,
  dispatchSkillAgentTask,
  fetchSkillAgentArtifactBlobUrl,
  getSkillAgentTaskById,
  getSkillAgentTasksMeta,
  listSkillAgentArtifacts,
  listSkillAgentTasks,
  markSkillAgentTaskDone,
  pushSkillAgentArtifactsToDrive,
  runSkillAgentTask,
  updateSkillAgentTask,
} from "../../api/Skills/skillAgentTasksApi";
import { listSkillFichas } from "../../api/Skills/skillFichasApi";
import {
  buildAgentTasksTree,
  taskClientLabel,
  TASK_STATUS_LABELS,
  TASK_TYPE_LABELS,
  taskStatusColor,
} from "./skillAgentTaskTree";
import SkillAgentTasksHelpDialog from "./SkillAgentTasksHelpDialog";

function TreeBranch({
  node,
  depth,
  expanded,
  toggle,
  selectedId,
  onSelectTask,
  statusFilter,
}) {
  const theme = useTheme();
  const open = expanded.has(node.id);
  const isSeason = node.kind === "season";
  const Icon = isSeason ? CalendarMonthOutlinedIcon : BusinessOutlinedIcon;
  const accent = isSeason ? theme.palette.primary.main : "#5c6bc0";

  const tasks = (node.tasks || []).filter(
    (t) => !statusFilter || t.Status === statusFilter,
  );
  const childCount =
    node.children?.reduce((s, c) => {
      const n = (c.tasks || []).filter(
        (t) => !statusFilter || t.Status === statusFilter,
      ).length;
      return s + n;
    }, 0) ?? tasks.length;

  if (node.children && childCount === 0 && statusFilter) return null;
  if (!node.children && tasks.length === 0 && statusFilter) return null;

  return (
    <Box>
      <Box
        onClick={() => toggle(node.id)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1,
          py: 0.85,
          pl: 1 + depth * 1.5,
          borderRadius: 2,
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { bgcolor: alpha(accent, 0.08) },
        }}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            color: "text.secondary",
            transform: open ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 0.15s",
          }}
        />
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1.5,
            display: "grid",
            placeItems: "center",
            bgcolor: alpha(accent, 0.12),
            color: accent,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 16 }} />
        </Box>
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ flex: 1, minWidth: 0 }}
          noWrap
        >
          {node.label}
        </Typography>
        <Chip
          size="small"
          label={childCount || node.count || 0}
          sx={{
            height: 22,
            fontWeight: 700,
            bgcolor: alpha(accent, 0.1),
            color: accent,
          }}
        />
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        {node.children?.map((child) => (
          <TreeBranch
            key={child.id}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            toggle={toggle}
            selectedId={selectedId}
            onSelectTask={onSelectTask}
            statusFilter={statusFilter}
          />
        ))}
        {tasks.map((task) => {
          const selected = Number(selectedId) === Number(task.id);
          return (
            <Box
              key={task.id}
              onClick={() => onSelectTask(task.id)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                ml: 1 + (depth + 1) * 1.5,
                mr: 0.5,
                px: 1.25,
                py: 1,
                mb: 0.35,
                borderRadius: 2,
                cursor: "pointer",
                border: "1px solid",
                borderColor: selected
                  ? alpha(theme.palette.primary.main, 0.45)
                  : "transparent",
                bgcolor: selected
                  ? alpha(theme.palette.primary.main, 0.1)
                  : alpha(theme.palette.background.paper, 0.5),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.07),
                },
              }}
            >
              <AssignmentOutlinedIcon
                sx={{
                  fontSize: 16,
                  color: selected ? "primary.main" : "text.disabled",
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {task.Titulo}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {TASK_TYPE_LABELS[task.TipoTarea] || task.TipoTarea}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={TASK_STATUS_LABELS[task.Status] || task.Status}
                color={taskStatusColor(task.Status)}
                sx={{ height: 22, maxWidth: 110 }}
              />
            </Box>
          );
        })}
      </Collapse>
    </Box>
  );
}

function parseArtifactMeta(artifact) {
  const raw = artifact?.MetaJson;
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function ArtifactThumb({ artifact, selected, onToggle }) {
  const isText =
    artifact.Kind === "text" || parseArtifactMeta(artifact).kind === "research";
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (isText) return undefined;
    let revoked = false;
    let objectUrl = "";
    (async () => {
      try {
        objectUrl = await fetchSkillAgentArtifactBlobUrl(artifact.id);
        if (!revoked) setSrc(objectUrl);
      } catch {
        if (!revoked) {
          setSrc("");
          setFailed(true);
        }
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [artifact.id, isText]);

  if (isText) {
    const meta = parseArtifactMeta(artifact);
    const brief = meta.brief || artifact.Title || "";
    return (
      <Box
        sx={{
          gridColumn: "1 / -1",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: alpha("#00838f", 0.04),
          p: 1.5,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mb: 0.75 }}
        >
          <Chip
            size="small"
            color="info"
            label="Research"
            sx={{ height: 22 }}
          />
          <Typography variant="subtitle2" fontWeight={800}>
            {artifact.Title || "Research radar"}
          </Typography>
          {Array.isArray(meta.brandsOk) && meta.brandsOk.length ? (
            <Typography variant="caption" color="text.secondary">
              {meta.brandsOk.join(" · ")}
            </Typography>
          ) : null}
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ whiteSpace: "pre-wrap", lineHeight: 1.45 }}
        >
          {brief}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      onClick={onToggle}
      sx={{
        border: "2px solid",
        borderColor: selected ? "primary.main" : "divider",
        borderRadius: 2,
        overflow: "hidden",
        cursor: "pointer",
        bgcolor: "background.paper",
        position: "relative",
      }}
    >
      <Box
        sx={{
          aspectRatio: "3 / 4",
          bgcolor: "action.hover",
          display: "grid",
          placeItems: "center",
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt={artifact.Title || artifact.FileName}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : failed ? (
          <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
            Sin preview
          </Typography>
        ) : (
          <CircularProgress size={28} />
        )}
      </Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ px: 1, py: 0.75 }}
      >
        <Checkbox size="small" checked={selected} sx={{ p: 0.25 }} />
        <Typography variant="caption" fontWeight={700} noWrap sx={{ flex: 1 }}>
          {artifact.Title || artifact.FileName}
        </Typography>
        {artifact.DriveFileId ? (
          <Chip
            size="small"
            label="Drive"
            color="success"
            sx={{ height: 20 }}
          />
        ) : null}
      </Stack>
    </Box>
  );
}

function TaskDetailPanel({ taskId, fichas, onChanged, onDeleted }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [task, setTask] = useState(null);
  const [form, setForm] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [autoUploadDrive, setAutoUploadDrive] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [pushMsg, setPushMsg] = useState("");
  const [pushing, setPushing] = useState(false);

  const loadArtifacts = useCallback(async () => {
    if (!taskId) return;
    try {
      const data = await listSkillAgentArtifacts(taskId);
      setArtifacts(Array.isArray(data?.artifacts) ? data.artifacts : []);
      if (data?.status && task && data.status !== task.Status) {
        const row = await getSkillAgentTaskById(taskId);
        setTask(row);
        onChanged?.(row);
      }
    } catch {
      /* ignore */
    }
  }, [taskId, task, onChanged]);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    setError("");
    setOkMsg("");
    try {
      const row = await getSkillAgentTaskById(taskId);
      setTask(row);
      setAutoUploadDrive(Boolean(row.AutoUploadDrive));
      setRunning(row.Status === "running");
      const c =
        row.ConstraintsJson && typeof row.ConstraintsJson === "object"
          ? row.ConstraintsJson
          : {};
      setForm({
        Titulo: row.Titulo || "",
        Brief: row.Brief || "",
        Temporada: row.Temporada || "",
        TipoTarea: row.TipoTarea || "otro",
        SkillFichaId: row.SkillFichaId ? String(row.SkillFichaId) : "",
        ConstraintsJson: {
          tipología: c.tipología || c.tipologia || "",
          coste: c.coste || "",
          mercado: c.mercado || "",
          refsAncla: c.refsAncla || "",
          numPropuestas:
            c.numPropuestas != null ? String(c.numPropuestas) : "6",
        },
      });
      try {
        const data = await listSkillAgentArtifacts(taskId);
        setArtifacts(Array.isArray(data?.artifacts) ? data.artifacts : []);
      } catch {
        setArtifacts([]);
      }
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo cargar.");
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!taskId || !running) return undefined;
    const t = setInterval(async () => {
      try {
        const row = await getSkillAgentTaskById(taskId);
        setTask(row);
        onChanged?.(row);
        if (row.Status !== "running") {
          setRunning(false);
          const data = await listSkillAgentArtifacts(taskId);
          setArtifacts(Array.isArray(data?.artifacts) ? data.artifacts : []);
          if (row.Status === "needs_review") {
            setOkMsg(row.ResultSummary || "Propuestas listas para revisar.");
          } else if (row.Status === "failed") {
            setError(row.ResultSummary || "Falló la generación.");
          }
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => clearInterval(t);
  }, [taskId, running, onChanged]);

  const readOnly = task && ["running", "done"].includes(task.Status);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const constraints = {
        ...form.ConstraintsJson,
        numPropuestas: Number(form.ConstraintsJson.numPropuestas) || 6,
      };
      const updated = await updateSkillAgentTask(taskId, {
        Titulo: form.Titulo,
        Brief: form.Brief,
        Temporada: form.Temporada,
        TipoTarea: form.TipoTarea,
        SkillFichaId: form.SkillFichaId ? Number(form.SkillFichaId) : undefined,
        ConstraintsJson: constraints,
      });
      setTask(updated);
      setOkMsg(
        updated?.PromptSnapshot
          ? "Guardado. Prompt técnico actualizado."
          : "Guardado.",
      );
      onChanged?.(updated);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    setSaving(true);
    setError("");
    setOkMsg("");
    try {
      await handleSaveInner();
      const res = await runSkillAgentTask(taskId, {
        autoUploadDrive,
        customerFolderName: taskClientLabel(task),
      });
      setTask(res.task);
      setRunning(true);
      setSelectedIds(new Set());
      setOkMsg(
        res.message ||
          "Generación iniciada. En 1–4 min verás las propuestas aquí.",
      );
      onChanged?.(res.task);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo lanzar.");
      setRunning(false);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveInner = async () => {
    const constraints = {
      ...form.ConstraintsJson,
      numPropuestas: Number(form.ConstraintsJson.numPropuestas) || 6,
    };
    const updated = await updateSkillAgentTask(taskId, {
      Titulo: form.Titulo,
      Brief: form.Brief,
      Temporada: form.Temporada,
      TipoTarea: form.TipoTarea,
      SkillFichaId: form.SkillFichaId ? Number(form.SkillFichaId) : undefined,
      ConstraintsJson: constraints,
    });
    setTask(updated);
    return updated;
  };

  const handleDispatch = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await dispatchSkillAgentTask(taskId, {
        promptOnly: true,
        customerFolderName: taskClientLabel(task),
      });
      setTask(res.task);
      setOkMsg(
        res.task?.DriveFolderUrl
          ? "Carpeta Drive lista (modo avanzado). Usa «Lanzar propuestas» para generar imágenes."
          : `Drive falló: ${
              res.driveNote ||
              res.driveStatus?.initError ||
              "revisa secrets / .env"
            }`,
      );
      onChanged?.(res.task);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo preparar.");
    } finally {
      setSaving(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handlePushDrive = async (all) => {
    setPushing(true);
    setSaving(true);
    setPushMsg("");
    setError("");
    setOkMsg("");
    try {
      if (!all && !selectedIds.size) {
        setPushMsg("error:Marca al menos una propuesta (clic en la imagen).");
        setError("Marca al menos una propuesta (clic en la imagen).");
        return;
      }
      setPushMsg("info:Subiendo a Drive…");
      const body = all
        ? { all: true, customerFolderName: taskClientLabel(task) }
        : {
            artifactIds: [...selectedIds],
            customerFolderName: taskClientLabel(task),
          };
      const res = await pushSkillAgentArtifactsToDrive(taskId, body);
      setArtifacts(Array.isArray(res.artifacts) ? res.artifacts : artifacts);
      if (res.task) {
        setTask(res.task);
        onChanged?.(res.task);
      }
      const n = res.uploaded?.length || 0;
      const msg = all
        ? `${n} propuestas subidas a Drive.`
        : `${n} propuestas guardadas en Drive.`;
      setOkMsg(msg);
      setPushMsg(`ok:${msg}`);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "No se pudo subir a Drive.";
      setError(msg);
      setPushMsg(`error:${msg}`);
    } finally {
      setPushing(false);
      setSaving(false);
    }
  };

  const handleDone = async () => {
    setSaving(true);
    try {
      const updated = await markSkillAgentTaskDone(taskId);
      setTask(updated);
      setOkMsg("Marcada como hecha.");
      onChanged?.(updated);
    } catch (err) {
      setError(err?.response?.data?.error || "Error.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("¿Cancelar esta tarea?")) return;
    setSaving(true);
    try {
      const updated = await cancelSkillAgentTask(taskId);
      setTask(updated);
      setRunning(false);
      onChanged?.(updated);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Eliminar esta tarea?")) return;
    try {
      await deleteSkillAgentTask(taskId);
      onDeleted?.(taskId);
    } catch {
      setError("No se pudo eliminar.");
    }
  };

  const copyPrompt = async () => {
    if (!task?.PromptSnapshot) return;
    try {
      await navigator.clipboard.writeText(task.PromptSnapshot);
      setOkMsg("Prompt copiado.");
    } catch {
      setError("No se pudo copiar.");
    }
  };

  if (loading || !form) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const canLaunch =
    task.Status !== "done" &&
    task.Status !== "cancelled" &&
    task.Status !== "running";

  const imageArtifacts = artifacts.filter(
    (a) => a.Kind !== "text" && parseArtifactMeta(a).kind !== "research",
  );

  return (
    <Stack spacing={2} sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ sm: "flex-start" }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={700}
          >
            Encargo #{task.id}
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.25 }}>
            {task.Titulo}
          </Typography>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ mt: 1 }}
            flexWrap="wrap"
            useFlexGap
          >
            <Chip
              size="small"
              icon={<CalendarMonthOutlinedIcon />}
              label={task.Temporada || "Sin temporada"}
              variant="outlined"
            />
            <Chip
              size="small"
              icon={<BusinessOutlinedIcon />}
              label={taskClientLabel(task)}
              variant="outlined"
            />
            <Chip
              size="small"
              label={TASK_STATUS_LABELS[task.Status] || task.Status}
              color={taskStatusColor(task.Status)}
            />
          </Stack>
        </Box>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Eliminar">
            <IconButton color="error" onClick={handleDelete} size="small">
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {okMsg && (
        <Alert severity="success" onClose={() => setOkMsg("")}>
          {okMsg}
        </Alert>
      )}
      {task.ResultSummary && task.Status === "failed" && (
        <Alert severity="error">{task.ResultSummary}</Alert>
      )}

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.background.paper, 0.85),
        }}
      >
        <Stack spacing={1.5}>
          <TextField
            label="Título"
            size="small"
            value={form.Titulo}
            disabled={readOnly}
            onChange={(e) => setForm((p) => ({ ...p, Titulo: e.target.value }))}
            sx={pageFieldSx}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <FormControl
              size="small"
              sx={{ flex: 1, ...pageFieldSx }}
              disabled={readOnly}
            >
              <InputLabel>Cliente / ficha</InputLabel>
              <Select
                label="Cliente / ficha"
                value={form.SkillFichaId}
                onChange={(e) =>
                  setForm((p) => ({ ...p, SkillFichaId: e.target.value }))
                }
              >
                <MenuItem value="">—</MenuItem>
                {fichas.map((f) => (
                  <MenuItem key={f.id} value={String(f.id)}>
                    {f.Titulo || f.SkillSlug}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 140, ...pageFieldSx }}
              disabled={readOnly}
            >
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={form.TipoTarea}
                onChange={(e) =>
                  setForm((p) => ({ ...p, TipoTarea: e.target.value }))
                }
              >
                {Object.entries(TASK_TYPE_LABELS).map(([k, label]) => (
                  <MenuItem key={k} value={k}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Temporada"
              size="small"
              value={form.Temporada}
              disabled={readOnly}
              onChange={(e) =>
                setForm((p) => ({ ...p, Temporada: e.target.value }))
              }
              sx={{ minWidth: 110, ...pageFieldSx }}
            />
          </Stack>
          <TextField
            label="Brief"
            multiline
            minRows={3}
            value={form.Brief}
            disabled={readOnly}
            onChange={(e) => setForm((p) => ({ ...p, Brief: e.target.value }))}
            sx={pageFieldSx}
          />
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            CONSTRAINTS
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
            {["tipología", "coste", "mercado", "refsAncla"].map((key) => (
              <TextField
                key={key}
                label={key}
                size="small"
                value={form.ConstraintsJson[key] || ""}
                disabled={readOnly}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    ConstraintsJson: {
                      ...p.ConstraintsJson,
                      [key]: e.target.value,
                    },
                  }))
                }
                sx={{ flex: 1, ...pageFieldSx }}
              />
            ))}
            <TextField
              label="Nº propuestas"
              size="small"
              type="number"
              inputProps={{ min: 1, max: 8 }}
              value={form.ConstraintsJson.numPropuestas || "6"}
              disabled={readOnly}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  ConstraintsJson: {
                    ...p.ConstraintsJson,
                    numPropuestas: e.target.value,
                  },
                }))
              }
              sx={{ width: 120, ...pageFieldSx }}
            />
          </Stack>

          <FormControlLabel
            control={
              <Checkbox
                checked={autoUploadDrive}
                disabled={readOnly}
                onChange={(e) => setAutoUploadDrive(e.target.checked)}
              />
            }
            label="Subir todo al terminar (pruebas rápidas)"
          />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {!readOnly && (
              <Button
                variant="outlined"
                startIcon={<SaveOutlinedIcon />}
                disabled={saving}
                onClick={handleSave}
              >
                Guardar
              </Button>
            )}
            {canLaunch && (
              <Button
                variant="contained"
                startIcon={
                  saving || running ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PlayArrowOutlinedIcon />
                  )
                }
                sx={pagePrimaryButtonSx}
                disabled={saving || running}
                onClick={handleRun}
              >
                Lanzar propuestas
              </Button>
            )}
            {task.Status === "running" && (
              <Chip
                icon={<CircularProgress size={14} />}
                label="Generando… (no cierres)"
                color="info"
              />
            )}
            {task.Status !== "done" && task.Status !== "cancelled" && (
              <Button
                color="success"
                variant="contained"
                startIcon={<CheckCircleOutlineIcon />}
                disabled={saving || task.Status === "running"}
                onClick={handleDone}
              >
                Hecha
              </Button>
            )}
            {task.Status !== "cancelled" && task.Status !== "done" && (
              <Button color="inherit" disabled={saving} onClick={handleCancel}>
                Cancelar
              </Button>
            )}
            {task.Status === "done" || task.Status === "cancelled" ? (
              <Button
                variant="outlined"
                disabled={saving}
                onClick={async () => {
                  setSaving(true);
                  try {
                    const updated = await updateSkillAgentTask(taskId, {
                      Status: "draft",
                    });
                    setTask(updated);
                    setRunning(false);
                    setOkMsg(
                      "Reabierta en borrador. Ya puedes lanzar de nuevo.",
                    );
                    onChanged?.(updated);
                  } catch (err) {
                    setError(
                      err?.response?.data?.error || "No se pudo reabrir.",
                    );
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                Reabrir
              </Button>
            ) : null}
            {task.DriveFolderUrl && (
              <Button
                startIcon={<OpenInNewOutlinedIcon />}
                href={task.DriveFolderUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir carpeta Drive
              </Button>
            )}
            <Button
              size="small"
              variant="text"
              disabled={saving || task.Status === "running"}
              onClick={handleDispatch}
            >
              Solo preparar carpeta
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 3,
          bgcolor: alpha("#1b5e20", 0.03),
          borderColor: alpha("#1b5e20", 0.14),
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          spacing={1}
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={800}>
              Resultados
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {imageArtifacts.length
                ? `${imageArtifacts.length} propuestas · marca las que valen y guarda en Drive`
                : artifacts.length
                  ? "Research listo · esperando imágenes"
                  : "Tras lanzar, las previews aparecen aquí"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                pushing ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CloudUploadOutlinedIcon />
                )
              }
              disabled={pushing || saving || !imageArtifacts.length}
              onClick={() => handlePushDrive(false)}
            >
              Guardar seleccionadas
              {selectedIds.size ? ` (${selectedIds.size})` : ""}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={
                pushing ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CloudUploadOutlinedIcon />
                )
              }
              disabled={pushing || saving || !imageArtifacts.length}
              onClick={() => handlePushDrive(true)}
            >
              Guardar todas
            </Button>
            <Button
              size="small"
              onClick={loadArtifacts}
              disabled={saving || pushing}
            >
              Actualizar
            </Button>
          </Stack>
        </Stack>

        {pushMsg ? (
          <Alert
            severity={
              pushMsg.startsWith("ok:")
                ? "success"
                : pushMsg.startsWith("info:")
                  ? "info"
                  : "error"
            }
            sx={{ mb: 1.5 }}
            onClose={() => setPushMsg("")}
          >
            {pushMsg.replace(/^(ok|error|info):/, "")}
          </Alert>
        ) : null}

        {task.Status === "running" && !artifacts.length ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Generando propuestas fotorealistas…
            </Typography>
          </Box>
        ) : artifacts.length ? (
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: {
                xs: "1fr 1fr",
                sm: "repeat(3, 1fr)",
                md: "repeat(4, 1fr)",
              },
            }}
          >
            {artifacts.map((a) => (
              <ArtifactThumb
                key={a.id}
                artifact={a}
                selected={selectedIds.has(a.id)}
                onToggle={() => toggleSelect(a.id)}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Sin propuestas aún. Guarda el brief y pulsa <b>Lanzar propuestas</b>
            .
          </Typography>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box
          onClick={() => setPromptOpen((v) => !v)}
          sx={{
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            bgcolor: alpha("#0d47a1", 0.04),
            "&:hover": { bgcolor: alpha("#0d47a1", 0.08) },
          }}
        >
          <ExpandMoreIcon
            sx={{
              transform: promptOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.15s",
              fontSize: 20,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Ver detalle técnico
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Prompt generado
              {task.SkillVersionUsed != null
                ? ` · skill v${task.SkillVersionUsed}`
                : ""}
              {task.DispatchMode ? ` · ${task.DispatchMode}` : ""}
            </Typography>
          </Box>
          {task.PromptSnapshot && (
            <Button
              size="small"
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={(e) => {
                e.stopPropagation();
                copyPrompt();
              }}
            >
              Copiar
            </Button>
          )}
        </Box>
        <Collapse in={promptOpen}>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: alpha("#000", 0.03),
              fontSize: "0.75rem",
              whiteSpace: "pre-wrap",
              maxHeight: 280,
              overflow: "auto",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            }}
          >
            {task.PromptSnapshot ||
              "Se genera al lanzar o al preparar la carpeta."}
          </Box>
        </Collapse>
      </Paper>
    </Stack>
  );
}

export default function SkillAgentTasksPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { contentMargin } = useContext(SidebarContext);

  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [fichas, setFichas] = useState([]);
  const [meta, setMeta] = useState(null);
  const [groupBy, setGroupBy] = useState("season");
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [selectedId, setSelectedId] = useState(
    () => searchParams.get("id") || "",
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTask, setNewTask] = useState({
    SkillFichaId: "",
    Titulo: "",
    Temporada: "",
    TipoTarea: "propuestas",
    Brief: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }
      const decoded = jwtDecode(token);
      const user = await getUserById(decoded.userId);
      if (!user?.Permiso_TareasAgentes) {
        setForbidden(true);
        return;
      }
      const [tasks, fichasData, metaData] = await Promise.all([
        listSkillAgentTasks({}),
        listSkillFichas({}),
        getSkillAgentTasksMeta(),
      ]);
      const list = Array.isArray(tasks) ? tasks : [];
      setRows(list);
      setFichas(Array.isArray(fichasData) ? fichasData : []);
      setMeta(metaData || null);

      // Expand tree nodes
      setExpanded((prev) => {
        const treeNow = buildAgentTasksTree(list, "season");
        const next = new Set(prev);
        treeNow.forEach((n1) => {
          next.add(n1.id);
          n1.children?.forEach((n2) => next.add(n2.id));
        });
        return next;
      });
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 403) setForbidden(true);
      else setError("No se pudieron cargar las tareas.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Abrir tarea de prueba Zara (#2) al entrar si no hay otra seleccionada
  useEffect(() => {
    if (loading || forbidden) return;
    const urlId = searchParams.get("id");
    if (urlId) {
      if (String(selectedId) !== String(urlId)) setSelectedId(String(urlId));
      return;
    }
    if (!selectedId && rows.some((t) => Number(t.id) === 2)) {
      setSelectedId("2");
      setSearchParams({ id: "2" }, { replace: true });
    }
  }, [loading, forbidden, rows, searchParams, selectedId, setSearchParams]);

  const filteredRows = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;
    return rows.filter((r) => {
      const blob = [r.Titulo, r.Brief, r.SkillSlug, r.Temporada, r.CustomerName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(qq);
    });
  }, [rows, q]);

  const tree = useMemo(
    () => buildAgentTasksTree(filteredRows, groupBy),
    [filteredRows, groupBy],
  );

  const driveOk = Boolean(meta?.drive?.configured);
  const sdkOk = Boolean(meta?.cursorSdkConfigured);

  const toggleNode = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectTask = (id) => {
    setSelectedId(String(id));
    setSearchParams({ id: String(id) });
  };

  const handleCreate = async () => {
    if (!newTask.Titulo.trim()) {
      setError("Pon un título.");
      return;
    }
    setCreating(true);
    setError("");
    try {
      const created = await createSkillAgentTask({
        SkillFichaId: newTask.SkillFichaId
          ? Number(newTask.SkillFichaId)
          : null,
        Titulo: newTask.Titulo.trim(),
        Temporada: newTask.Temporada.trim() || null,
        TipoTarea: newTask.TipoTarea,
        Brief: newTask.Brief,
      });
      setCreateOpen(false);
      setNewTask({
        SkillFichaId: "",
        Titulo: "",
        Temporada: "",
        TipoTarea: "propuestas",
        Brief: "",
      });
      await load();
      selectTask(created.id);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo crear.");
    } finally {
      setCreating(false);
    }
  };

  if (forbidden) {
    return (
      <Box sx={{ ...contentMargin, p: 3 }}>
        <Alert severity="warning">
          No tienes permiso para Tareas Agentes. Pide activar{" "}
          <b>Permiso_TareasAgentes</b>.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        height: "100dvh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: alpha(theme.palette.primary.main, 0.025),
        backgroundImage: `radial-gradient(1000px 400px at 0% -20%, ${alpha(theme.palette.primary.main, 0.09)}, transparent)`,
        ...contentMargin,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 1.5, md: 2.5 },
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: alpha(theme.palette.background.paper, 0.92),
          backdropFilter: "blur(10px)",
          zIndex: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: "primary.main",
              }}
            >
              <FolderOpenOutlinedIcon />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800}>
                Tareas de agentes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Árbol Temporada → Cliente → Encargo · igual que en Drive
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              icon={
                driveOk ? <CloudDoneOutlinedIcon /> : <CloudOffOutlinedIcon />
              }
              label={driveOk ? "Drive listo" : "Drive pendiente"}
              color={driveOk ? "success" : "default"}
            />
            <Chip
              size="small"
              icon={<SmartToyOutlinedIcon />}
              label={sdkOk ? "SDK automático" : "Modo manual"}
              color={sdkOk ? "info" : "warning"}
              variant="outlined"
            />
            <Button
              variant="outlined"
              startIcon={<HelpOutlineOutlinedIcon />}
              onClick={() => setHelpOpen(true)}
              size="small"
            >
              Instrucciones
            </Button>
            <Button
              variant="outlined"
              startIcon={<DescriptionOutlinedIcon />}
              onClick={() => navigate("/skills")}
              size="small"
            >
              Fichas
            </Button>
            <Button
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              sx={pagePrimaryButtonSx}
              onClick={() => setCreateOpen(true)}
            >
              Nuevo encargo
            </Button>
          </Stack>
        </Stack>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mx: 2, mt: 1.5, flexShrink: 0 }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {/* Body: tree + detail */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(300px, 380px) 1fr",
          },
          gap: 0,
        }}
      >
        {/* Tree panel */}
        <Paper
          square
          elevation={0}
          sx={{
            borderRight: { md: "1px solid" },
            borderBottom: { xs: "1px solid", md: "none" },
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            minHeight: { xs: 280, md: 0 },
            maxHeight: { xs: "42dvh", md: "none" },
            bgcolor: alpha(theme.palette.background.paper, 0.7),
          }}
        >
          <Box
            sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={groupBy}
              onChange={(_, v) => v && setGroupBy(v)}
              fullWidth
              sx={{
                mb: 1.25,
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontWeight: 700,
                  py: 0.75,
                },
              }}
            >
              <ToggleButton value="season">Por temporada</ToggleButton>
              <ToggleButton value="client">Por cliente</ToggleButton>
            </ToggleButtonGroup>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                placeholder="Buscar…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1, ...pageFieldSx }}
              />
              <FormControl size="small" sx={{ minWidth: 120, ...pageFieldSx }}>
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {Object.entries(TASK_STATUS_LABELS).map(([k, label]) => (
                    <MenuItem key={k} value={k}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
            {loading ? (
              <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            ) : tree.length === 0 ? (
              <Box sx={{ py: 6, px: 2, textAlign: "center" }}>
                <FolderOpenOutlinedIcon
                  sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
                />
                <Typography fontWeight={700} color="text.secondary">
                  Sin encargos
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Crea el primero para empezar el árbol.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddOutlinedIcon />}
                  sx={pagePrimaryButtonSx}
                  onClick={() => setCreateOpen(true)}
                >
                  Nuevo encargo
                </Button>
              </Box>
            ) : (
              tree.map((node) => (
                <TreeBranch
                  key={node.id}
                  node={node}
                  depth={0}
                  expanded={expanded}
                  toggle={toggleNode}
                  selectedId={selectedId}
                  onSelectTask={selectTask}
                  statusFilter={statusFilter}
                />
              ))
            )}
          </Box>
        </Paper>

        {/* Detail panel */}
        <Box
          sx={{
            minHeight: 0,
            overflow: "auto",
            bgcolor: alpha(theme.palette.background.default, 0.35),
          }}
        >
          {!selectedId ? (
            <Box
              sx={{
                height: "100%",
                minHeight: 320,
                display: "grid",
                placeItems: "center",
                p: 4,
                textAlign: "center",
              }}
            >
              <Box sx={{ maxWidth: 420 }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: 4,
                    mx: "auto",
                    mb: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: "primary.main",
                  }}
                >
                  <AssignmentOutlinedIcon sx={{ fontSize: 36 }} />
                </Box>
                <Typography variant="h6" fontWeight={800}>
                  Elige un encargo
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  Navega por <b>temporada</b> y <b>cliente</b> a la izquierda.
                  Al preparar un encargo se crea la misma ruta en Google Drive.
                </Typography>
              </Box>
            </Box>
          ) : (
            <TaskDetailPanel
              key={`${selectedId}-${
                rows.find((r) => Number(r.id) === Number(selectedId))?.Status ||
                ""
              }`}
              taskId={selectedId}
              fichas={fichas}
              onChanged={(updated) => {
                setRows((prev) =>
                  prev.map((r) =>
                    Number(r.id) === Number(updated.id)
                      ? { ...r, ...updated }
                      : r,
                  ),
                );
              }}
              onDeleted={(id) => {
                setRows((prev) =>
                  prev.filter((r) => Number(r.id) !== Number(id)),
                );
                setSelectedId("");
                setSearchParams({});
              }}
            />
          )}
        </Box>
      </Box>

      <SkillAgentTasksHelpDialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Nuevo encargo</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <FormControl size="small" fullWidth sx={pageFieldSx}>
              <InputLabel>Cliente / ficha</InputLabel>
              <Select
                label="Cliente / ficha"
                value={newTask.SkillFichaId}
                onChange={(e) =>
                  setNewTask((p) => ({ ...p, SkillFichaId: e.target.value }))
                }
              >
                <MenuItem value="">Elegir…</MenuItem>
                {fichas.map((f) => (
                  <MenuItem key={f.id} value={String(f.id)}>
                    {f.Titulo || f.SkillSlug}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1.5}>
              <FormControl size="small" sx={{ flex: 1, ...pageFieldSx }}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  label="Tipo"
                  value={newTask.TipoTarea}
                  onChange={(e) =>
                    setNewTask((p) => ({ ...p, TipoTarea: e.target.value }))
                  }
                >
                  {Object.entries(TASK_TYPE_LABELS).map(([k, label]) => (
                    <MenuItem key={k} value={k}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Temporada"
                placeholder="SS27"
                value={newTask.Temporada}
                onChange={(e) =>
                  setNewTask((p) => ({ ...p, Temporada: e.target.value }))
                }
                sx={{ width: 120, ...pageFieldSx }}
              />
            </Stack>
            <TextField
              size="small"
              label="Título"
              placeholder="Ej. 3 propuestas bomber"
              value={newTask.Titulo}
              onChange={(e) =>
                setNewTask((p) => ({ ...p, Titulo: e.target.value }))
              }
              fullWidth
              sx={pageFieldSx}
            />
            <TextField
              size="small"
              label="Brief"
              multiline
              minRows={3}
              value={newTask.Brief}
              onChange={(e) =>
                setNewTask((p) => ({ ...p, Brief: e.target.value }))
              }
              fullWidth
              sx={pageFieldSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            sx={pagePrimaryButtonSx}
            disabled={creating}
            onClick={handleCreate}
          >
            {creating ? "Creando…" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
